/**
 * SOURCE CONNECTION STATE → HEBREW. 07 §6.2 (freshness), 07 N-5 / N-6 (configuration), 07 DG-1/DG-2.
 *
 * ── THE ONE DISTINCTION THIS FILE EXISTS TO PROTECT ─────────────────────────────────────────────
 *
 * `NOT_CONFIGURED` is not a kind of `ERROR`. They answer different questions and they are fixed by
 * different people:
 *
 *   NOT_CONFIGURED — this connection was never set up. Nothing is broken. Somebody has to supply
 *                    the fields, and the screen names EVERY missing one, not the first.
 *   ERROR          — this connection was set up and it is not working now. The office is waiting on
 *                    a fix, and until it arrives no number from it should be trusted.
 *
 * 07 N-5 measures what collapsing them costs: a missing `data_source_type` silently resolved to a
 * working default on 5 of 313 companies, in two separate code paths, so five unconfigured companies
 * behaved exactly like five configured ones. One amber "problem" chip covering both is that defect
 * with a nicer font.
 *
 * ── AND THE SECOND ONE: TWO WATERMARKS, NOT ONE "LAST UPDATED" ──────────────────────────────────
 *
 * 07 §6.2 keeps `dataAsOfUtc` (the latest business date actually present) apart from `fetchedAtUtc`
 * (when we last successfully talked to the source), because a successful pull that returns nothing
 * advances the second and must not advance the first. The file-mtime version of that mistake is the
 * mechanism that let a canonical freeze stay invisible for a month. So the headline freshness line
 * quotes the DATA watermark, and the fetch time is shown separately and labelled as what it is.
 *
 * ── NO SLA IS `UNKNOWN`, NEVER `FRESH` ──────────────────────────────────────────────────────────
 *
 * `assessStaleness` returns `UNKNOWN` when there is no `expectedByUtc`, and this file gives that
 * state its own sentence. An unmeasurable SLA reported as healthy is the shape of a health
 * aggregator that was AMBER all day and reached nobody.
 *
 * This module is pure. It maps a server-stated verdict to words. It computes no verdict of its own,
 * and it never decides whether a connection may be used — that is the server's answer, carried in
 * `state`.
 */

import type {
  CompletenessSummary,
  FreshnessSummary,
  SourceConnectionState,
  SourceConnectionSummary,
  SourceFailureKind,
  StalenessState,
} from './contract';
import { formatInstantHe } from './adminVocabulary';
import type { Tone } from './adminVocabulary';

export interface SourceStatusText {
  /** The chip word. 09 §9.5 A3: colour never carries the meaning on its own. */
  readonly labelHe: string;
  /** Exactly one sentence, in office Hebrew. Never a diagnostic, never a vendor's own words. */
  readonly explanationHe: string;
  /** Exactly one next step. There is no connection state with zero next steps. */
  readonly nextStepHe: string;
  readonly tone: Tone;
}

/**
 * The three states, before the failure kind narrows them.
 *
 * `NOT_CONFIGURED`'s next step is the only one addressed to the office rather than to support: it
 * is the only one a person in the office can actually finish.
 */
const STATE_TEXT: Readonly<Record<SourceConnectionState, SourceStatusText>> = {
  NOT_CONFIGURED: {
    labelHe: 'לא הוגדר',
    explanationHe: 'החיבור הזה מעולם לא הוגדר, ולכן לא התקבלו ממנו נתונים.',
    nextStepHe: 'השלמת פרטי החיבור',
    tone: 'muted',
  },
  OK: {
    labelHe: 'תקין',
    explanationHe: 'החיבור פעיל והנתונים מתקבלים ממנו.',
    nextStepHe: 'אין פעולה נדרשת',
    tone: 'ok',
  },
  ERROR: {
    labelHe: 'תקלה',
    explanationHe: 'החיבור הוגדר ואינו עובד כרגע.',
    nextStepHe: 'פנייה לתמיכה עם שם החיבור',
    tone: 'error',
  },
};

/**
 * The failure kinds, in office Hebrew.
 *
 * The `diagnostic` string that travels beside each of these in `SourceFailure` is deliberately NOT
 * rendered: it is for an operator and for the audit trail, no caller may parse or branch on one,
 * and it is the one place a vendor's own words can appear. A customer sees the classification.
 */
const FAILURE_TEXT: Readonly<Record<SourceFailureKind, { readonly explanationHe: string; readonly nextStepHe: string }>> = {
  NOT_CONFIGURED: {
    explanationHe: 'חסרים פרטים בהגדרת החיבור.',
    nextStepHe: 'השלמת פרטי החיבור',
  },
  NOT_PROVEN: {
    explanationHe: 'החיבור מוגדר, אך מעולם לא הוכח שהוא מביא נתונים מהמערכת הזו.',
    nextStepHe: 'הרצת בדיקת חיבור מול המערכת',
  },
  UNREACHABLE: {
    explanationHe: 'לא הצלחנו להגיע למערכת המקור.',
    nextStepHe: 'בדיקה מול האחראי על מערכת המקור',
  },
  NOT_AUTHENTICATED: {
    explanationHe: 'המערכת שבצד השני לא זיהתה את פרטי ההזדהות של החיבור.',
    nextStepHe: 'חידוש פרטי ההזדהות של החיבור',
  },
  NOT_AUTHORIZED_AT_SOURCE: {
    explanationHe: 'ההזדהות התקבלה, אך אין לה הרשאה לנתוני החברה במערכת המקור.',
    nextStepHe: 'בקשת הרשאה לנתוני החברה במערכת המקור',
  },
  CAPABILITY_NOT_SUPPLIED: {
    explanationHe: 'מערכת המקור אינה מספקת את סוג הנתונים הזה.',
    nextStepHe: 'בדיקה אילו נתונים החיבור הזה אמור לספק',
  },
  EXTERNAL_COMPANY_UNKNOWN: {
    explanationHe: 'החברה המקושרת אינה קיימת יותר במערכת המקור.',
    nextStepHe: 'עדכון הקישור בין החברה למערכת המקור',
  },
  REJECTED_AT_BOUNDARY: {
    explanationHe: 'חלק מהנתונים שהתקבלו לא עברו את בדיקות הקליטה ולא נכנסו למערכת.',
    nextStepHe: 'צפייה ברשומות שנדחו',
  },
  ADAPTER_FAULT: {
    explanationHe: 'אירעה תקלה בתוכנת החיבור עצמה.',
    nextStepHe: 'פתיחת פנייה לתמיכה עם שם החיבור',
  },
};

/**
 * The one mapping function for a connection's headline state.
 *
 * `failureKind` narrows the sentence; it never changes the LABEL, because the label is the answer to
 * "was this ever set up", and no diagnostic detail may quietly turn a never-configured connection
 * into an amber one.
 */
export function sourceStatusTextFor(connection: SourceConnectionSummary): SourceStatusText {
  const base = STATE_TEXT[connection.state];
  if (connection.failureKind === null) return base;
  const detail = FAILURE_TEXT[connection.failureKind];
  return { ...base, explanationHe: detail.explanationHe, nextStepHe: detail.nextStepHe };
}

/**
 * The missing-fields sentence. EVERY absent field, never the first (07 N-5).
 *
 * The field names are the configuration contract's own (`endpointRef`, `secretRef`, `textEncoding`,
 * `sourceDateFormat`) and they are given office Hebrew here for the same reason a deny reason is:
 * an operator who is shown `secretRef` has been shown a column name, not an instruction.
 */
const CONFIGURATION_FIELD_HE: Readonly<Record<string, string>> = {
  endpointRef: 'שם היעד הלוגי',
  secretRef: 'הפניה לסוד ההזדהות',
  textEncoding: 'קידוד התווים',
  sourceDateFormat: 'מבנה התאריך במקור',
};

export function configurationFieldHe(field: string): string | null {
  return CONFIGURATION_FIELD_HE[field] ?? null;
}

/**
 * What is still missing, written out.
 *
 * A field this build has no Hebrew for is DROPPED from the sentence and counted instead, so a new
 * server-side field can never put an English identifier on a customer's screen (09 §9.1 V3).
 */
export function missingConfigurationHe(fields: readonly string[]): string {
  if (fields.length === 0) return '';
  const named = fields.map(configurationFieldHe).filter((f): f is string => f !== null);
  if (named.length === 0) return `חסרים ${fields.length} פרטים בהגדרת החיבור.`;
  const tail = fields.length - named.length;
  const list = named.join(' · ');
  return tail === 0
    ? `חסר להשלמה: ${list}.`
    : `חסר להשלמה: ${list} · ועוד ${tail} פרטים.`;
}

// ── Freshness ─────────────────────────────────────────────────────────────────────────────────────

export interface FreshnessText {
  readonly labelHe: string;
  /** "נתונים נכון ל־…" — or the written absence. NEVER a date the product does not have. */
  readonly asOfHe: string;
  /** The fetch watermark, labelled as what it is so it cannot be read as the data watermark. */
  readonly fetchedHe: string | null;
  readonly tone: Tone;
}

const STALENESS_HE: Readonly<Record<StalenessState, { readonly labelHe: string; readonly tone: Tone }>> = {
  FRESH: { labelHe: 'עדכני', tone: 'ok' },
  LAGGING: { labelHe: 'מפגר אחרי הלוח', tone: 'warn' },
  STALE: { labelHe: 'לא עודכן במועד', tone: 'error' },
  // No schedule and no SLA ⇒ there is no basis for a verdict, and saying so is the whole point.
  UNKNOWN: { labelHe: 'אין בסיס לקביעת טריות', tone: 'warn' },
};

export function stalenessHe(state: StalenessState): { readonly labelHe: string; readonly tone: Tone } {
  return STALENESS_HE[state];
}

/**
 * The freshness line.
 *
 * `null` freshness is the never-observed case and it gets its OWN sentence — not `UNKNOWN`, which
 * means "we have data and no basis to judge it", and not a zero date.
 */
export function freshnessTextFor(freshness: FreshnessSummary | null): FreshnessText {
  if (freshness === null) {
    return {
      labelHe: 'טרם התקבלו נתונים',
      asOfHe: 'לא התקבלו נתונים מהחיבור הזה מעולם.',
      fetchedHe: null,
      tone: 'muted',
    };
  }

  const term = STALENESS_HE[freshness.state];
  const dataAsOf = formatInstantHe(freshness.dataAsOfUtc);
  const fetchedAt = formatInstantHe(freshness.fetchedAtUtc);

  return {
    labelHe: term.labelHe,
    asOfHe:
      dataAsOf === null
        ? // A connection that has been reached but has produced no business date. It is not fresh
          // and it is not broken; it is a source that answered with nothing, and 07 §6.2 says a
          // zero-row pull must never be allowed to look like an update.
          'התקבלה תשובה מהמקור, אך אין בה תאריך עסקי כלשהו.'
        : `נתונים נכון ל־${dataAsOf}`,
    fetchedHe: fetchedAt === null ? null : `הפנייה האחרונה למקור: ${fetchedAt}`,
    tone: term.tone,
  };
}

// ── Completeness (07 DG-1 / DG-2) ─────────────────────────────────────────────────────────────────

/**
 * 07 DG-2: *"A total is never rendered over an incomplete set without saying so."* This is the
 * "saying so", in words a bookkeeper reads rather than a reason code.
 */
const PARTIAL_HE: Readonly<Record<string, string>> = {
  unbalanced: 'הנתונים שהתקבלו אינם מאוזנים.',
  rows_rejected_at_boundary: 'חלק מהשורות נדחו בקליטה ואינן נכללות.',
  page_limit_reached: 'התקבל רק חלק מהנתונים — נעצרה שליפה ארוכה.',
  value_absent_in_set: 'חסרים ערכים בחלק מהשורות.',
};

const EMPTY_HE: Readonly<Record<string, string>> = {
  no_data_for_period: 'אין נתונים לתקופה שנבחרה.',
  capability_not_supplied: 'מערכת המקור אינה מספקת את סוג הנתונים הזה.',
  source_returned_nothing: 'מערכת המקור החזירה תשובה ריקה.',
};

export interface CompletenessText {
  readonly labelHe: string;
  readonly explanationHe: string;
  readonly tone: Tone;
  /** DG-2: `true` ⇒ any total shown over this set must carry the qualification above. */
  readonly totalRequiresQualification: boolean;
}

export function completenessTextFor(completeness: CompletenessSummary): CompletenessText {
  if (completeness.kind === 'COMPLETE') {
    return {
      labelHe: 'מלא',
      explanationHe: 'כל הנתונים לתקופה התקבלו.',
      tone: 'ok',
      totalRequiresQualification: false,
    };
  }
  if (completeness.kind === 'PARTIAL') {
    return {
      labelHe: 'חלקי',
      explanationHe: PARTIAL_HE[completeness.reason] ?? 'הנתונים שהתקבלו חלקיים.',
      tone: 'warn',
      totalRequiresQualification: true,
    };
  }
  return {
    labelHe: 'ריק',
    explanationHe: EMPTY_HE[completeness.reason] ?? 'לא התקבלו נתונים.',
    tone: 'warn',
    totalRequiresQualification: true,
  };
}

/** Exposed for the mirror test only. */
export function mappedFailureKinds(): readonly string[] {
  return Object.keys(FAILURE_TEXT);
}

/** Exposed for the mirror test only. */
export function mappedStalenessStates(): readonly string[] {
  return Object.keys(STALENESS_HE);
}

/** Exposed for the mirror test only. */
export function mappedPartialReasons(): readonly string[] {
  return Object.keys(PARTIAL_HE);
}

/** Exposed for the mirror test only. */
export function mappedEmptyReasons(): readonly string[] {
  return Object.keys(EMPTY_HE);
}
